"use client";

import React, { useLayoutEffect, useRef, useState } from "react";
import { Star } from "lucide-react";
import { cn } from "@/src/lib/utils";
import { reviewService } from "../services";
import type { IPublicReview } from "../types";

interface IPublicReviewCardProps {
  review: IPublicReview;
  /** Clamp the body (4 lines on mobile, 3 on desktop) and offer "Show more". */
  clamp?: boolean;
  onShowMore?: () => void;
}

/**
 * One review. Mobile is the app's card (avatar 40, stars on their own row, 14px body);
 * md+ is Airbnb's (avatar 48, "★★★★★ · July 2026" meta line, 16px body).
 */
const PublicReviewCard: React.FC<IPublicReviewCardProps> = ({
  review,
  clamp = false,
  onShowMore,
}) => {
  const name = reviewService.authorName(review.author);
  const tenure = reviewService.authorTenure(review.author?.months_on_platform);
  const month = reviewService.reviewMonth(review.created_at);
  const stars = Math.round(review.stars ?? 0);
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const [overflows, setOverflows] = useState(false);

  // "Show more" only when the clamp actually cut the text (re-checked on resize, as
  // the clamp is 4 lines on mobile and 3 on desktop).
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!clamp || !el) return;
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [clamp, review.body]);

  const starRow = (size: number, gap: string) => (
    <span className={cn("flex", gap)} aria-label={`${stars} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          className={
            n <= stars
              ? "fill-[#222222] text-ink"
              : "fill-[#DDDDDD] text-ink-faint"
          }
        />
      ))}
    </span>
  );

  return (
    <article className="flex flex-col">
      <div className="flex items-center gap-3">
        {review.author?.image_url ? (
          <img
            src={review.author.image_url}
            alt=""
            className="h-10 w-10 shrink-0 rounded-full object-cover md:h-12 md:w-12"
          />
        ) : (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#D6D6D6] text-base font-semibold text-ink-dark md:h-12 md:w-12 md:text-lg">
            {name.charAt(0).toUpperCase()}
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate text-base font-medium text-ink md:font-semibold">
            {name}
          </p>
          {tenure && (
            <p className="text-13 text-ink-secondary md:text-sm">{tenure}</p>
          )}
        </div>
      </div>

      {/* Mobile (app): stars alone. Desktop (Airbnb): small stars · month. */}
      <div className="mt-3 md:hidden">{starRow(12.5, "gap-[3.5px]")}</div>
      <div className="mt-3 hidden items-center gap-1.5 text-sm text-ink md:flex">
        {starRow(10, "gap-px")}
        {month && (
          <>
            <span aria-hidden className="text-ink-secondary">
              ·
            </span>
            <span className="font-medium">{month}</span>
          </>
        )}
      </div>

      {review.body && (
        <p
          ref={bodyRef}
          className={cn(
            "mt-3 whitespace-pre-line text-sm leading-5 text-ink md:mt-2 md:text-base md:leading-6",
            clamp && "line-clamp-4 md:line-clamp-3",
          )}
        >
          {/* A mention filter's matched sentences are highlighted, as in the app. */}
          {reviewService
            .highlightSegments(review.body, review.highlights)
            .map((segment, index) =>
              segment.highlighted ? (
                <mark
                  key={index}
                  className="rounded-sm bg-[#E5E7EB] px-0.5 font-semibold text-ink [box-decoration-break:clone]"
                >
                  {segment.text}
                </mark>
              ) : (
                <React.Fragment key={index}>{segment.text}</React.Fragment>
              ),
            )}
        </p>
      )}
      {clamp && overflows && onShowMore && (
        <button
          type="button"
          onClick={onShowMore}
          className="mt-3 self-start text-xs font-medium text-ink underline underline-offset-2 md:mt-4 md:text-base md:font-semibold"
        >
          Show more
        </button>
      )}
    </article>
  );
};

export default PublicReviewCard;
